import { Typography } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Step } from 'react-joyride'
import Tour from 'ui/components/Tour/Tour'
import { getStatusIcon } from './OptimizationsDropdown'

interface FilterTourProps {
  runTour: null | number
  setRunTour: (state: boolean) => void
}

const OptimizationsTour = ({ runTour, setRunTour }: FilterTourProps) => {
  const { t } = useTranslation()

  const steps: Array<Step> = [
    {
      target: 'choose_optimization',
      placement: 'bottom',
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'To be able to view an optimization, you must first select an optimization from this dropdown menu. When an optimization has been created it receives several statuses until it is finished, defined by the symbol to the far right.'
            )}
          </Typography>
          <br />
          <Typography variant="h4">
            <b>{getStatusIcon('Finished')}</b>
            {t('Successful optimization, a result has been presented in the Production Plan module.')}
          </Typography>
          <br />
          <Typography variant="h4">
            <b>{getStatusIcon('Failed')}</b>
            {t('Unsuccessful optimization, no result exists.')}
          </Typography>
          <br />
          <Typography variant="h4">
            <b>{getStatusIcon('Running')}</b>
            {t('Ongoing optimization, just created.')}
          </Typography>
          <br />
          <Typography variant="h6">
            <i className="fas fa-comment-exclamation" />
            <i>
              {t(
                'As of now, you can only select the latest 50 optimizations overall, across production plan, follow-up incl. deviations and settlement. (This module is in continuous development and any input is appreciated!)'
              )}
            </i>
          </Typography>
          <br />
        </div>
      ),
      title: t('Select an optimization to view'),
    },
    {
      target: 'card_content',
      placement: 'auto',
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'After selecting an optimization you will be presented by several cards, all with different types of information to help with troubleshooting or understanding the optimizations better.'
            )}
            <br />
            <b>
              {t(
                'The data we show here is in its raw form and might hence not be very comprehensible in its current state.'
              )}
            </b>
            <br />
          </Typography>
          <br />

          <Typography variant="h6">
            <i className="fas fa-comment-exclamation" />
            <i>
              {t(
                'As stated before, this module is in continuous development and any input is appreciated! If you miss any information about the optimization, do not hesitate to contact us.'
              )}
            </i>
          </Typography>
        </div>
      ),
      title: t('Optimization data'),
    },
    {
      target: 'solver_hint',
      placement: 'auto',
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'When an optimization fails and gets the status "Unsolvable", the solver may return a hint pointing at the constraint it could not satisfy. This is a clue for troubleshooting, not a diagnosis of the actual cause.'
            )}
          </Typography>
          <br />
          <Typography variant="h6">
            {t(
              'Use the "Where to take it" list to see what to check next, and download the JSON if you need to hand the hint over to someone else.'
            )}
          </Typography>
        </div>
      ),
      title: t('Solver hint'),
    },
    {
      target: 'error_message',
      placement: 'auto',
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'These are the error messages generated from the optimization job of the optimization you selected. In its current state, even successfull optimizations might generate error messages.'
            )}
          </Typography>
          <br />
          <Typography variant="h6">
            {t(
              'The error messages might give a clue about in what direction to investigate further when troubleshooting a failed optimization.'
            )}
          </Typography>
        </div>
      ),
      title: t('Error messages'),
    },
    {
      target: 'settings_comparison',
      placement: 'left-start',
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'This card shows the difference in settings between the optimization job you are currently reviewing and the most recent finished optimization job of the same type.'
            )}
          </Typography>
          <br />
          <Typography variant="h4">
            <span style={{ backgroundColor: '#D9FDD3' }}>{`${t('Green background')}: `}</span>
            {t(
              'Means that this setting was newly created, or was added in the later hours of the optimization compared to the most recent finished optimization job of the same type.'
            )}
          </Typography>
          <br />
          <Typography variant="h4">
            <span style={{ backgroundColor: '#FDD3D3' }}>{`${t('Red background')}: `}</span>
            {t(
              'Means that this setting was removed or changed in a way that is not compatible with the most recent finished optimization job of the same type.'
            )}
          </Typography>
          <br />

          <Typography variant="h4">
            <span style={{ fontWeight: 'bold' }}>{`Priority 0: `}</span>
            {t(
              'Default values, the values that are specified in the model applied if no other values are specified in other settings.'
            )}
          </Typography>

          <Typography variant="h4">
            <span style={{ fontWeight: 'bold' }}>{`Priority 10: `}</span>
            {t('Base settings, the values from these settings are active if no deviation setting is specified.')}
          </Typography>
          <Typography variant="h4">
            <span style={{ fontWeight: 'bold' }}>{`Priority 30: `}</span>
            {t('Deviation settings, these are the settings that are active.')}
          </Typography>
        </div>
      ),
      title: t('Settings comparison'),
    },
    {
      target: 'debug_log',
      placement: 'auto',

      content: (
        <div>
          <Typography variant="h6">
            {t(
              "This is the place to document your findings for the selected optimization. By sharing log messages on what you've discovered, you can help your team understand the optimization process better and make informed decisions based on your insights. The messages are sorted in chronological order, with the most recent messages appearing at the bottom."
            )}
          </Typography>
          <br />
          <Typography variant="h4">
            <b>{`${t('Fetch new log messages')}: `}</b>
            {t(
              'Refetches log messages in case one of your colleagues have added new log messages since you opened the module.'
            )}
          </Typography>
          <br />
          <Typography variant="h4">
            <b>{`${t('Log data')}: `}</b>
            {t(
              'By adding log data in JSON format, you can provide additional context and details about your findings. This structured data can help your team analyze and interpret the information more effectively. For example, you can copy and paste settings or input data into this editor.'
            )}
          </Typography>
        </div>
      ),
      title: t('Debug log'),
    },
    {
      target: 'input_data',
      placement: 'auto',
      disableScrolling: false,
      content: (
        <div>
          <Typography variant="h6">
            {t(
              'This is the input data that was used for the optimization job you are currently reviewing. This is mainly intended for advanced users who want to dive deeper into the technical details of the optimization process.'
            )}
          </Typography>
        </div>
      ),
      title: t('Input data'),
    },
    {
      target: 'forecasts',
      placement: 'auto',
      disableScrolling: false,

      content: (
        <div>
          <Typography variant="h6">
            {t(
              'This is the forecasts card, here you can see all forecasts that were used as input to the optimization job you are currently reviewing.'
            )}
          </Typography>
        </div>
      ),
      title: t('Forecasts'),
    },
    {
      target: 'measurement_values',
      placement: 'auto',
      disableScrolling: false,

      content: (
        <div>
          <Typography variant="h6">
            {t(
              'This is the measurement values for your system and what they looked like at the time of the start of the selected optimization.'
            )}
          </Typography>
        </div>
      ),
      title: t('Measurement values'),
    },
  ]

  return (
    <Tour
      isSingle={!!runTour}
      run={runTour !== null}
      setRunState={setRunTour}
      steps={steps}
      dialogWidth={window.innerWidth < 1280 ? 1200 : undefined}
    />
  )
}

export default OptimizationsTour
